import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { accessApi } from "../../../api/access";
import type { ApiError } from "../../../api/client";

type Option = { id: string; label: string };

/** A bounded server search inside the directory's own read permission. */
export function AccessEntityPicker({ kind, label, value, onChange }: { kind: "USER" | "COMPANY"; label: string; value: string; onChange: (id: string) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<Option | null>(null);
  const [options, setOptions] = useState<Option[]>([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(0);
  const [retry, setRetry] = useState(0);

  useEffect(() => { if (!value) { setPicked(null); setQuery(""); } }, [value]);
  useEffect(() => {
    if (!open) return;
    let live = true;
    setBusy(true); setError("");
    const timer = setTimeout(() => {
      const request = kind === "USER"
        ? accessApi.users({ q: query }, 0, 20).then((p) => ({ total: p.totalElements, options: p.content.map((u) => ({ id: u.id, label: `${u.firstName} ${u.lastName} · ${u.email}` })) }))
        : accessApi.companies(query, 0, 20).then((p) => ({ total: p.totalElements, options: p.content.map((c) => ({ id: c.id, label: c.name })) }));
      void request.then((result) => { if (live) { setOptions(result.options); setTotal(result.total); setActive(0); } })
        .catch((e: ApiError) => { if (live) { setError(e.message); setOptions([]); } })
        .finally(() => { if (live) setBusy(false); });
    }, 200);
    return () => { live = false; clearTimeout(timer); };
  }, [kind, query, open, retry]);
  useEffect(() => {
    const outside = (event: MouseEvent) => { if (!container.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  function pick(option: Option) { setPicked(option); setQuery(""); setOpen(false); onChange(option.id); }
  return <div ref={container} className="access-picker">
    <label htmlFor={id}>{label}</label>
    <div className="access-picker-input"><input id={id} role="combobox" aria-expanded={open} aria-controls={`${id}-options`} aria-autocomplete="list" aria-activedescendant={open && !busy && options[active] ? `${id}-${active}` : undefined}
      value={open ? query : picked?.label ?? ""} placeholder={t("access.all", "All")} onFocus={() => setOpen(true)} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
      onKeyDown={(event) => {
        if (event.key === "Escape") { setOpen(false); event.stopPropagation(); }
        if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); setActive((n) => Math.min(n + 1, options.length - 1)); }
        if (event.key === "ArrowUp") { event.preventDefault(); setActive((n) => Math.max(n - 1, 0)); }
        if (event.key === "Enter" && open && !busy && options[active]) { event.preventDefault(); pick(options[active]); }
      }} />
      {value && <button type="button" className="access-picker-clear" aria-label={`${t("action.clear", "Clear")}: ${label}`} onClick={() => { setPicked(null); setQuery(""); onChange(""); }}>×</button>}
    </div>
    {open && <div id={`${id}-options`} className="access-picker-options" role="listbox" aria-label={label} aria-busy={busy}>
      {busy ? <div className="sub" role="status">{t("common.loading", "Loading…")}</div> : error ? <div role="alert">{error}<button className="btn" type="button" onClick={() => setRetry((n) => n + 1)}>{t("action.retry", "Try again")}</button></div> : <>
        {total > options.length && <div className="sub">{t("access.refineSearch", "More results are available. Type more to narrow the search.")} ({options.length}/{total})</div>}
        {!options.length && <div className="sub">{t("common.noMatches", "Nothing matches this filter.")}</div>}
        {options.map((option, index) => <button id={`${id}-${index}`} type="button" role="option" aria-selected={value === option.id} className={index === active ? "is-active" : ""} key={option.id} onMouseEnter={() => setActive(index)} onClick={() => pick(option)}>{option.label}</button>)}
      </>}
    </div>}
  </div>;
}
